import connect from 'components/Shared/connect';
import { checkoutForm, getAddressInfo } from 'views/CheckoutPage/selector';
import { resetSection, Fields, Field, EventWithDataHandler } from 'redux-form';
import * as rules from 'views/CheckoutPage/validation';
import autoForm, {
  AutoFormOwnProps,
  AutoFormMergedProps,
  QuickStartFormProps,
  AddressFormProps
} from '../../BGH-RD3/AutoForm';
import { RenderAddress } from '../AutoForm/renderAddress';
import { CustomerType } from 'models/CustomerType';
import {
  deliveryAddressChanged,
  setAddressDetails,
  onBlurCheckout
} from 'views/CheckoutPage/actions';
import { rm, NO_ELEMENT } from 'utils';
import { ChangeEvent } from 'react';
import ContactInfoPolicy from 'views/CheckoutPage/PolicyInformation/themes/BGH-RD3/contactInfoPolicy';
import { RenderCheckbox, RenderLabeledInput } from 'components/Shared/Form/inputs';
import { LeaveMessage } from '../../BGH-RD3/leaveMessage';
import { AddressInfo } from 'models/AddressInfo';
import cn from 'classnames';
import { postGetAddressInfo } from 'services/ApiCheckoutPageService';
import { isMobileLayout } from 'components/Shared/responsive';
import batchAction from 'store/batching';
import { reloadCart } from 'components/Cart/actions';
import RotForm from './rotForm';
import { LoaderButton } from 'components/Button';
import commonStyles from '../styles.scss';
import { CertificateForm } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/certificateForm';
import { IconArrow } from 'components/Shared/Icon';
import { BaseRenderWrapperError } from 'components/Shared/Form/renderWrapperError/themes/BGH-RD3';
import { ToggleQuickstart } from '../../BGH-RD3/toggleQuickstart';

interface QuickStartFormMergedprops extends QuickStartFormProps {
  isMobile: boolean;
  form: AddressInfo;
}

const QuickStartForm = connect<QuickStartFormMergedprops, QuickStartFormProps>(state => ({
  isMobile: isMobileLayout(state),
  form: getAddressInfo(state)
}))(({
  onChange,
  inputHandler,
  noAddressError,
  isMobile,
  loading,
  handleSubmit,
  submitDisabled,
  form,
  toggleQuickStart
}: QuickStartFormMergedprops) => {
  const hasFetchedAddress =
    !!form.firstName && !!form.lastName && !!form.city && !!form.street && !!form.zipCode;

  if (hasFetchedAddress) {
    return NO_ELEMENT;
  }

  return (
    <div className={commonStyles.quickStartForm}>
      <Field
        name="phoneNumber"
        inputMode="numeric"
        pattern={isMobile ? '[0-9]*' : rules.phoneNumberRegexString}
        validate={[rules.phone, () => noAddressError]}
        component={RenderLabeledInput}
        onChange={onChange}
        onKeyPress={inputHandler}
        props={{
          customLabel: rm('checkout_cellphone'),
          CustomErrorComponent: () => (
            <div>
              <BaseRenderWrapperError error={rm('Checkout_error_cellphone')} />
              <ToggleQuickstart
                onClick={toggleQuickStart}
                label={rm('checkout_contact_info_address_manual_without_ssn')}
              />
            </div>
          )
        }}
      />
      <Field
        name="email"
        type="email"
        validate={rules.email}
        component={RenderLabeledInput}
        onChange={onChange}
        onKeyPress={inputHandler}
        props={{
          customLabel: rm('checkout_email_rd3')
        }}
      />
      <LoaderButton
        loading={loading}
        className={commonStyles.getAddressButton}
        disabled={submitDisabled}
        onClick={handleSubmit}
        appearance="quaternary"
      >
        {rm('checkout_identity_button')} <IconArrow size="tiny" />
      </LoaderButton>
    </div>
  );
});

interface MergedProps extends AddressFormProps {
  hasRotDeduction: boolean;
  deliveryAddressChanged: EventWithDataHandler<ChangeEvent>;
  onBlur: EventWithDataHandler<ChangeEvent>;
  address: AddressInfo;
  isMobile: boolean;
}

const AddressForm = connect<MergedProps>(
  state => ({
    hasRotDeduction: state.cart.hasRotDeduction,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    address: checkoutForm(state, 'address'),
    isMobile: isMobileLayout(state)
  }),
  dispatch => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onBlur: (_e: ChangeEvent, n: any, _p: any, name: string) => dispatch(onBlurCheckout(name, n)),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deliveryAddressChanged: (_e: ChangeEvent, n: any, _p: any, name: string) =>
      dispatch(deliveryAddressChanged(n, name))
  })
)(({
  hasRotDeduction,
  address,
  isMobile,
  deliveryAddressChanged,
  onBlur,
  resetForm
}: MergedProps) => {
  return (
    <>
      <div className={commonStyles.quickStartFormTop}>
        <div className={commonStyles.address}>
          <Fields
            names={['firstName', 'lastName', 'street', 'city', 'zipCode', 'county']}
            label={rm('checkout_address')}
            component={RenderAddress(resetForm)}
          />
        </div>
        <div className={commonStyles.policy}>
          <ContactInfoPolicy desktop marginBottom />
        </div>
        <div className={commonStyles.altAddress}>
          <Field
            name="hasDeliveryAddress"
            onChange={deliveryAddressChanged}
            component={RenderCheckbox}
            label={rm('checkout_delivery_alternative_label')}
            className={commonStyles.checkboxWrapper}
          />
        </div>
      </div>

      <Field
        name="phoneNumber"
        type="tel"
        validate={rules.phone}
        className={cn(commonStyles.input, commonStyles.left)}
        component={RenderLabeledInput}
        onBlur={onBlur}
        autoFocus
        props={{ customLabel: rm('checkout_cellphone') }}
        inputMode="tel"
      />
      <Field
        name="email"
        type="email"
        validate={rules.email}
        className={cn(commonStyles.input, commonStyles.right)}
        component={RenderLabeledInput}
        onBlur={onBlur}
        value={address.email}
        props={{ customLabel: rm('checkout_email_rd3') }}
      />
      <Field
        name="identityNumber"
        inputMode="numeric"
        pattern={isMobile ? '[0-9]*' : rules.identityNumberRegexString}
        className={cn(commonStyles.input, commonStyles.left)}
        validate={rules.identityNumber}
        component={RenderLabeledInput}
        onBlur={onBlur}
        props={{
          customLabel: rm('checkout_identityheader_private')
        }}
      />
      <p className={cn(commonStyles.indentityHelp, commonStyles.input, commonStyles.right)}>
        {rm('checkout_identityheader_help')}
      </p>
      <LeaveMessage className={commonStyles.message} onBlur={onBlur} />
      {hasRotDeduction && <RotForm isManual={false} onBlur={onBlur} />}
      <CertificateForm />
      <ContactInfoPolicy mobile />
    </>
  );
});

interface StateProps {
  address: AddressInfo;
  isMobile: boolean;
}

export default connect<AutoFormMergedProps, AutoFormOwnProps, StateProps>(
  state => ({
    address: checkoutForm(state, 'address') as AddressInfo,
    isMobile: isMobileLayout(state)
  }),
  dispatch => ({
    setAddressDetails: (args: AddressInfo) =>
      dispatch(batchAction(setAddressDetails(args), reloadCart())),
    reset: () => dispatch(resetSection('checkout', 'address.deliveryAddress'))
  }),
  ({ address, isMobile, ...state }, { loadDetails, setAddressDetails, ...dispatch }, ownProps) => ({
    ...state,
    ...dispatch,
    ...ownProps,
    isValid:
      address && rules.isValidPhoneNumber(address.phoneNumber) && rules.isValidEmail(address.email),
    showAddressForm: Boolean(address && address.street),
    hasDeliveryAddress: Boolean(address && address.hasDeliveryAddress),
    loadDetails: () =>
      postGetAddressInfo({
        customerType: CustomerType.Private,
        number: address.phoneNumber
      }),
    setAddressDetails: (args: AddressInfo) =>
      setAddressDetails({
        ...args,
        phoneNumber: address && address.phoneNumber,
        email: address && address.email
      })
  })
)(autoForm(AddressForm, QuickStartForm));
